import "@/styles/login.css";
import { FLIGHT_DEALS_ONLY } from "@/lib/release";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatAuthError, formatApiErrorDetail } from "@/lib/api";
import { ArrowLeft, KeyRound, Loader2, Mail, Mountain, Smartphone } from "lucide-react";

function formatLoginError(err) {
  const detail = err.response?.data?.detail;
  if (detail === "invalid_credentials" || err.response?.status === 401) return "Incorrect email/phone or password.";
  return formatAuthError(err);
}

export default function Login() {
  const { login, requestPasswordRecovery, verifyRecoveryOtp, clearPhoneOtp, completePasswordRecovery } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState(() => new URLSearchParams(location.search).get("recovery") === "1" ? "recovery" : "password");
  const [identifier, setIdentifier] = useState("");
  const [recoveryIdentifier, setRecoveryIdentifier] = useState("");
  const [otp, setOtp] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [password, setPassword] = useState("");
  const [notice, setNotice] = useState("");
  const [recoveryChannel, setRecoveryChannel] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => () => clearPhoneOtp?.(), [clearPhoneOtp]);

  const goNext = () => {
    const next = new URLSearchParams(location.search).get("next");
    if (FLIGHT_DEALS_ONLY) { nav(next === "/flight-deals" || next?.startsWith("/flight-deals?") || next === "/prefetched-deals" || next?.startsWith("/prefetched-deals?") ? next : "/flight-deals"); return; }
    nav(next && next.startsWith("/") ? next : "/");
  };

  const backToSignIn = () => {
    clearPhoneOtp?.();
    setMode("password");
    setError("");
    setNotice("");
    setOtp("");
    setResetToken("");
    setNewPassword("");
    setRecoveryChannel("");
    setLoading(false);
  };

  const channelFor = (value) => (String(value || "").includes("@") ? "email" : "phone");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(""); setNotice("");
    try {
      await login(identifier, password);
      goNext();
    } catch (err) { setError(formatLoginError(err)); }
    finally { setLoading(false); }
  };

  const startRecovery = async (e) => {
    e.preventDefault();
    setLoading(true); setError(""); setNotice("");
    try {
      const channel = channelFor(recoveryIdentifier);
      setRecoveryChannel("");
      const data = await requestPasswordRecovery(recoveryIdentifier);
      setRecoveryChannel(data.channel || channel);
      setOtp("");
      setNotice(channel === "email" ? "Recovery link sent." : "OTP sent.");
    } catch (err) { setError(formatAuthError(err)); }
    finally { setLoading(false); }
  };

  const resendRecovery = async () => {
    setLoading(true); setError(""); setNotice(""); setRecoveryChannel("");
    try {
      const channel = recoveryChannel || channelFor(recoveryIdentifier);
      const data = await requestPasswordRecovery(recoveryIdentifier);
      setRecoveryChannel(data.channel || channel);
      setOtp("");
      setNotice(channel === "email" ? "Link resent." : "OTP resent.");
    } catch (err) { setError(formatAuthError(err)); }
    finally { setLoading(false); }
  };

  const verifyOtp = async () => {
    setLoading(true); setError("");
    try {
      const data = await verifyRecoveryOtp(recoveryIdentifier, otp);
      setResetToken(data.reset_token);
      setNotice("OTP verified.");
    } catch (err) { setError(formatAuthError(err)); }
    finally { setLoading(false); }
  };

  const finishRecovery = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) { setError("Password must be at least 8 characters."); return; }
    setLoading(true); setError("");
    try {
      await completePasswordRecovery(resetToken, newPassword);
      goNext();
    } catch (err) { setError(formatAuthError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="flight-login antialiased" style={{ fontFamily: "Inter, sans-serif" }}>
      <div className="flight-login-inner">
        <header className="flight-login-header">
        <Link to="/landing" data-testid="login-home-link" className="flight-login-brand">
          <span className="flight-login-brand-mark">
            <Mountain className="h-7 w-7" />
          </span>
          <span className="text-lg font-extrabold tracking-tight" style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}>TrailPulse</span>
        </Link>
        <Link to="/" className="flight-login-back"><ArrowLeft size={15} aria-hidden="true" />Back to flight deals</Link>
        </header>

        <main className="flight-login-board">
        <section className="flight-login-panel">
          <p className="flight-login-eyebrow">{mode === "password" ? "Welcome back" : "Let’s get you back in"}</p>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}>{mode === "password" ? "Sign in" : "Reset password"}</h1>
          <p className="mt-1 text-sm text-[#5F7267]">{mode === "password" ? "Your next journey starts here. Use your email or phone." : "Use your account’s email or phone to recover access."}</p>

          {mode === "password" ? (
            <form onSubmit={submit} data-testid="login-form">
              <label htmlFor="login-email" className="mt-6 block text-xs font-medium text-[#5F7267]">Email or phone</label>
              <input id="login-email" data-testid="login-email" type="text" inputMode="email" placeholder="you@company.com or +91..." value={identifier}
                onChange={(e) => setIdentifier(e.target.value)} autoComplete="username" required
                className="mt-1.5 min-h-10 w-full rounded-xl border border-[#BACABD] bg-[#FFFEF9] px-4 py-2.5 text-[#193B2E] outline-none transition-colors focus:border-[#E5B85C] focus:ring-2 focus:ring-[#E5B85C]" />

              <label htmlFor="login-password" className="mt-4 block text-xs font-medium text-[#5F7267]">Password</label>
              <input id="login-password" data-testid="login-password" type="password" placeholder="Password" value={password}
                onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required
                className="mt-1.5 min-h-10 w-full rounded-xl border border-[#BACABD] bg-[#FFFEF9] px-4 py-2.5 text-[#193B2E] outline-none transition-colors focus:border-[#E5B85C] focus:ring-2 focus:ring-[#E5B85C]" />

              <button type="button" data-testid="login-forgot-password-link" onClick={() => { setMode("recovery"); setError(""); setNotice(""); setOtp(""); setResetToken(""); setNewPassword(""); setRecoveryChannel(""); setRecoveryIdentifier(identifier); }}
                className="mt-3 inline-flex min-h-9 items-center text-sm font-semibold text-[#28634D] hover:text-[#163D2B]">
                Forgot password?
              </button>

              {error && <div data-testid="login-error" className="mt-4 rounded-lg border border-[#E5695B]/30 bg-[#E5695B]/10 px-3 py-2 text-sm text-[#AE392D]">{error}</div>}

              <button data-testid="login-submit" disabled={loading} type="submit"
                className="mt-4 flex min-h-11 w-full items-center justify-center gap-2 rounded-full bg-[#E5B85C] py-3 font-bold text-[#17211D] transition-colors hover:bg-[#f0c96f] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E5B85C] focus-visible:ring-offset-2 focus-visible:ring-offset-[#FAF9F3] disabled:opacity-60">
                {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Signing in...</> : <><KeyRound className="h-4 w-4" /> Sign in</>}
              </button>
            </form>
          ) : resetToken ? (
            <form onSubmit={finishRecovery}>
              <label htmlFor="login-new-password" className="mt-6 block text-xs font-medium text-[#5F7267]">New password</label>
              <input id="login-new-password" type="password" placeholder="New password" value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)} required minLength={8}
                className="mt-1.5 min-h-10 w-full rounded-xl border border-[#BACABD] bg-[#FFFEF9] px-4 py-2.5 text-[#193B2E] outline-none transition-colors focus:border-[#E5B85C] focus:ring-2 focus:ring-[#E5B85C]" />
              {notice && <div className="mt-4 rounded-lg border border-[#3FA98C]/30 bg-[#3FA98C]/10 px-3 py-2 text-sm text-[#256148]">{notice}</div>}
              {error && <div data-testid="login-error" className="mt-4 rounded-lg border border-[#E5695B]/30 bg-[#E5695B]/10 px-3 py-2 text-sm text-[#AE392D]">{error}</div>}
              <button disabled={loading} type="submit" className="mt-6 flex min-h-11 w-full items-center justify-center gap-2 rounded-full bg-[#E5B85C] py-3 font-bold text-[#17211D] disabled:opacity-60">
                {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving...</> : "Save password"}
              </button>
              <button type="button" onClick={backToSignIn}
                className="mt-4 flex min-h-10 w-full items-center justify-center rounded-full border border-[#BACABD] font-bold text-[#193B2E] hover:bg-[#E8EEDF]">
                Back to sign in
              </button>
            </form>
          ) : (
            <form onSubmit={startRecovery}>
              <label htmlFor="recovery-identifier" className="mt-6 block text-xs font-medium text-[#5F7267]">Email or phone</label>
              <input id="recovery-identifier" type="text" inputMode="email" placeholder="you@company.com or +91..." value={recoveryIdentifier}
                onChange={(e) => {
                  clearPhoneOtp?.(); setRecoveryIdentifier(e.target.value); setRecoveryChannel(""); setNotice(""); setOtp("");
                }} required disabled={loading}
                className="mt-1.5 min-h-10 w-full rounded-xl border border-[#BACABD] bg-[#FFFEF9] px-4 py-2.5 text-[#193B2E] outline-none transition-colors focus:border-[#E5B85C] focus:ring-2 focus:ring-[#E5B85C]" />
              {channelFor(recoveryIdentifier) === "phone" && <p className="mt-2 text-xs text-[#5F7267]">By requesting a code, you agree to Google processing your phone number for authentication and abuse prevention.</p>}

              {notice && (
                <div className="mt-4 rounded-lg border border-[#3FA98C]/30 bg-[#3FA98C]/10 px-3 py-2 text-sm text-[#256148]">
                  <p>{notice}</p>
                  {recoveryChannel === "email"
                    ? <p className="mt-2 flex items-center gap-2"><Mail className="h-4 w-4" /> Check your email.</p>
                    : <p className="mt-2 flex items-center gap-2"><Smartphone className="h-4 w-4" /> Enter OTP.</p>}
                </div>
              )}

              {notice && recoveryChannel === "phone" && (
                <>
                  <label htmlFor="recovery-otp" className="mt-4 block text-xs font-medium text-[#5F7267]">Phone OTP</label>
                  <div className="mt-1.5 flex gap-2">
                    <input id="recovery-otp" type="text" inputMode="numeric" placeholder="6-digit code" value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      className="min-h-10 min-w-0 flex-1 rounded-xl border border-[#BACABD] bg-[#FFFEF9] px-4 py-2.5 text-[#193B2E] outline-none transition-colors focus:border-[#E5B85C] focus:ring-2 focus:ring-[#E5B85C]" />
                    <button type="button" onClick={verifyOtp} aria-label="Verify phone code" title="Verify phone code" disabled={loading || !otp} className="min-h-10 rounded-full bg-[#E5B85C] px-4 font-bold text-[#17211D] disabled:opacity-60">
                      <Smartphone className="h-4 w-4" />
                    </button>
                  </div>
                </>
              )}

              {error && <div data-testid="login-error" className="mt-4 rounded-lg border border-[#E5695B]/30 bg-[#E5695B]/10 px-3 py-2 text-sm text-[#AE392D]">{error}</div>}

              {!notice ? (
                <>
                {error === formatApiErrorDetail("account_not_found") && <Link to="/register" className="mt-3 block text-sm font-semibold text-[#28634D] underline">Create account</Link>}
                <button disabled={loading} type="submit" className="mt-6 flex min-h-11 w-full items-center justify-center gap-2 rounded-full bg-[#E5B85C] py-3 font-bold text-[#17211D] disabled:opacity-60">
                  {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending...</> : (channelFor(recoveryIdentifier) === "email" ? "Send recovery" : "Send OTP")}
                </button>
                </>
              ) : (
                <button type="button" onClick={resendRecovery} disabled={loading}
                  className="mt-6 flex min-h-11 w-full items-center justify-center rounded-full bg-[#E5B85C] py-3 font-bold text-[#17211D] hover:bg-[#f0c96f] disabled:opacity-60">
                  {loading ? "Sending..." : "Resend"}
                </button>
              )}

              <button type="button" onClick={backToSignIn}
                className="mt-4 flex min-h-10 w-full items-center justify-center rounded-full border border-[#BACABD] font-bold text-[#193B2E] hover:bg-[#E8EEDF]">
                Back to sign in
              </button>
            </form>
          )}

          <p className="mt-6 text-center text-sm text-[#5F7267]">
            New to TrailPulse?{" "}
            <Link to="/register" data-testid="login-to-register" className="font-semibold text-[#28634D] hover:text-[#163D2B]">Create an account</Link>
          </p>
        </section>
        </main>
        <footer className="flight-login-footer"><span>? {new Date().getFullYear()} TrailPulse</span><span>Need a hand? <a href="mailto:hello@trailpulse.in">hello@trailpulse.in</a></span></footer>
      </div>
    </div>
  );
}
